import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col } from 'react-bootstrap';
import { Typography, Box } from '@material-ui/core';
import _ from 'lodash';

// Reusing components from the main dashboard
import { ReportsChart, StatisticCard } from './ReportsViews';
import Loader3Orange from '../../general/Loader3Orange';
import { FaUserFriends, FaCheckCircle, FaArchive, FaTasks } from 'react-icons/fa';

// Helper to format data for Chart.js Pie/Doughnut charts
const preparePieData = (data, labelKey, dataKey) => {
    if (!data || data.length === 0) return null;
    const labels = data.map(item => item[labelKey]);
    const chartData = data.map(item => item[dataKey]);
    
    // Consistent and pleasant color palette
    const backgroundColors = [
      '#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', 
      '#858796', '#5a5c69', '#fd7e14', '#6610f2', '#e83e8c'
    ];

    return {
        labels,
        datasets: [{
            data: chartData,
            backgroundColor: labels.map((_, i) => backgroundColors[i % backgroundColors.length]),
        }],
    };
};

// Helper to format data for Chart.js Bar/Line charts
const prepareLineBarData = (data, labelKey, datasetsConfig) => {
    if (!data || data.length === 0) return null;
    const labels = data.map(item => item[labelKey]);
    
    const datasets = datasetsConfig.map(config => ({
        label: config.label,
        data: data.map(item => _.get(item, config.dataKey, 0)),
        backgroundColor: config.backgroundColor,
        borderColor: config.borderColor,
        stack: config.stack, // For stacked bar charts
    }));

    return { labels, datasets };
};


const ProspectsTab = ({ brokerInfo, startDate, endDate, selectedStatus, selectedBroker, selectedBusinessSegment, translationsDashboard }) => {
    const [loading, setLoading] = useState(true);
    const [statsData, setStatsData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('show broker info',brokerInfo)
        // Ensure we have the necessary info before fetching
        const brokerageId = brokerInfo?.information?.brokeragecode;
        if (!brokerageId) {
            setError("Brokerage information is not available.");
            setLoading(false);
            return;
        }

        const fetchProspectsStats = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("jwt");

                // Construct the payload, sending 'all' if filter is empty
                const payload = {
                    startDate: startDate,
                    endDate: endDate,
                    status: !_.isEmpty(selectedStatus) ? selectedStatus : 'all',
                    brokerId: !_.isEmpty(selectedBroker) ? selectedBroker : null,
                    vertical: !_.isEmpty(selectedBusinessSegment) ? selectedBusinessSegment : null,
                };
                
                const response = await axios.post(
                    `${process.env.REACT_APP_BASICSERVICE}/api/prospects/${brokerageId}/prospects/stats`,
                    payload,
                    { headers: { "x-auth-token": token } }
                );
                
                setStatsData(response.data);

            } catch (err) {
                console.error("Error fetching prospect stats:", err.response || err);
                setError("Failed to load prospect statistics. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchProspectsStats();

    }, [brokerInfo, startDate, endDate, selectedStatus, selectedBroker, selectedBusinessSegment]);

    if (loading) {
        return <Loader3Orange />;
    }

    if (error) {
        return (
            <Box p={3} style={{ textAlign: 'center' }}>
                <img width={"300px"} src="https://error404.fun/img/illustrations/20.png" alt="Error"/>
                <Typography color="error" variant="h6">{error}</Typography>
            </Box>
        );
    }

    if (!statsData || _.isEmpty(statsData.kpis) || statsData.kpis.total === 0) {
        return (
            <Col md={12} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: '5rem' }}>
                <div><img width={"400px"} src="https://error404.fun/img/illustrations/08.png" alt="No Data"/></div>
                <div><h3 style={{ fontFamily: "GibsonSemiBd", color: "rgb(63, 68, 90)" }}>{translationsDashboard.noDataFound}</h3></div>
            </Col>
        );
    }
    
    // Prepare data for the charts
    const statusChartData = preparePieData(statsData.charts?.byStatus, 'status', 'count');
    const createdOverTimeData = prepareLineBarData(statsData.charts?.createdOverTime, 'date', 
        [{ label: 'New Prospects', dataKey: 'count', backgroundColor: 'rgba(54, 162, 235, 0.6)', borderColor: 'rgba(54, 162, 235, 1)' }]
    );
    const verticalChartData = prepareLineBarData(statsData.charts?.byVertical, 'vertical',
        [{ label: 'Total Prospects', dataKey: 'total', backgroundColor: 'rgba(255, 159, 64, 0.6)' }]
    );
    const salesByBrokerData = prepareLineBarData(statsData.charts?.salesByBroker, 'name',
        [{ label: 'Premium', dataKey: 'premium', backgroundColor: 'rgba(75, 192, 192, 0.6)' }]
    );


    return (
        <Box p={3}>
            {/* KPI Cards Row */}
            <div className="chartTitle" style={{justifyContent: 'center', marginBottom: '1rem'}}>
                {translationsDashboard.summary || 'Summary'}
            </div>
            <Row className="mb-4">
                <Col>
                    <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: '20px' }}>
                        <StatisticCard cardInfo={{ icon: FaUserFriends, title: 'Total Prospects', data: statsData.kpis?.total || 0 }} />
                        <StatisticCard cardInfo={{ icon: FaTasks, title: 'Active Prospects', data: statsData.kpis?.active || 0 }} />
                        <StatisticCard cardInfo={{ icon: FaCheckCircle, title: 'Binded Policies', data: statsData.kpis?.binded || 0 }} />
                        <StatisticCard cardInfo={{ icon: FaArchive, title: 'Archived', data: statsData.kpis?.archived || 0 }} />
                    </div>
                </Col>
            </Row>

            <Row>
                <Col md={6} className="mb-4">
                     <div className="chartTitle">{'Prospects by Status'}</div>
                     {statusChartData ? (
                        <ReportsChart reportData={statusChartData} reportChartType="DOUGHNUT" />
                     ) : <Typography align="center" className="mt-4">No status data available.</Typography>}
                </Col>
                <Col md={6} className="mb-4">
                    <div className="chartTitle">{'Prospects by Vertical'}</div>
                    {verticalChartData ? (
                       <ReportsChart reportData={verticalChartData} reportChartType="BAR" />
                    ) : <Typography align="center" className="mt-4">No vertical data available.</Typography>}
                </Col>
            </Row>

            <Row>
                <Col md={12} className="mb-4">
                    <div className="chartTitle">{'New Prospects Over Time'}</div>
                    {createdOverTimeData ? (
                        <ReportsChart reportData={createdOverTimeData} reportChartType="LINE" legend={false} />
                    ) : <Typography align="center" className="mt-4">No time-series data available.</Typography>}
                </Col>
            </Row>

            <Row>
                <Col md={12} className="mb-4">
                     <div className="chartTitle">{'Top Broker Performance (by Premium)'}</div>
                     {salesByBrokerData && salesByBrokerData.labels.length > 0 ? (
                        <ReportsChart reportData={salesByBrokerData} reportChartType="BAR" legend={false} />
                     ) : <Typography align="center" className="mt-4">No sales data available for brokers.</Typography>}
                </Col>
            </Row>
        </Box>
    );
};

export default ProspectsTab;